import React, {useEffect, useMemo, useState} from 'react';
import {StyleSheet, css} from 'aphrodite';
import {useLocation, useParams, useNavigate} from 'react-router-dom';
import {FiArrowLeft, FiClock, FiCoffee, FiMinus, FiStar} from 'react-icons/fi';

import api from '../api/api';
import {useCart} from '../context/CartContext';
import CartBannerWeb from '../components/CartBannerWeb';

const logo = '/logo192.png';

const PLACEHOLDER_IMAGE =
  'https://via.placeholder.com/1200x520.png?text=YaBatho+Restaurant';

const CATEGORY_ORDER = ['Breakfast', 'Starters', 'Main Course', 'Fast Food', 'Burgers', 'Pizza', 'Chicken', 'Seafood', 'Salads', 'Sides', 'Desserts', 'Drinks', 'Combos', 'Specials', 'More from this outlet'];
const CATEGORY_ALIASES = {starter: 'Starters', starters: 'Starters', main: 'Main Course', 'main course': 'Main Course', mains: 'Main Course', fastfood: 'Fast Food', 'fast food': 'Fast Food', burger: 'Burgers', burgers: 'Burgers', pizza: 'Pizza', pizzas: 'Pizza', chicken: 'Chicken', seafood: 'Seafood', salad: 'Salads', salads: 'Salads', side: 'Sides', sides: 'Sides', dessert: 'Desserts', desserts: 'Desserts', drink: 'Drinks', drinks: 'Drinks', beverage: 'Drinks', beverages: 'Drinks', combo: 'Combos', combos: 'Combos', special: 'Specials', specials: 'Specials', breakfast: 'Breakfast'};
const CATEGORY_KEYWORDS = [
  {category: 'Drinks', words: ['coke', 'fanta', 'sprite', 'water', 'juice', 'soda', 'drink', 'coffee', 'tea', 'milkshake']},
  {category: 'Burgers', words: ['burger', 'cheeseburger', 'hamburger', 'slider']},
  {category: 'Pizza', words: ['pizza', 'margherita', 'pepperoni']},
  {category: 'Chicken', words: ['chicken', 'wings', 'drumstick', 'quarter leg']},
  {category: 'Seafood', words: ['fish', 'prawn', 'seafood', 'calamari', 'hake']},
  {category: 'Sides', words: ['chips', 'fries', 'side', 'pap', 'rice', 'samp']},
  {category: 'Desserts', words: ['cake', 'dessert', 'ice cream', 'malva', 'pudding']},
  {category: 'Breakfast', words: ['breakfast', 'egg', 'omelette']},
  {category: 'Combos', words: ['combo', 'meal', 'special']},
];

const inferCategory = item => {
  const category = typeof item?.category === 'string' ? item.category.trim() : '';
  if (category) return CATEGORY_ALIASES[category.toLowerCase()] || category;
  const text = `${item?.name || ''} ${item?.description || ''}`.toLowerCase();
  return CATEGORY_KEYWORDS.find(group => group.words.some(word => text.includes(word)))?.category || 'More from this outlet';
};

const normalizePrice = price => {
  if (price === null || price === undefined || price === '') {
    return 0;
  }

  const cleaned =
    typeof price === 'string' ? price.replace(',', '.').trim() : price;

  const numericPrice = Number(cleaned);

  if (Number.isNaN(numericPrice)) {
    return 0;
  }

  if (Number.isInteger(numericPrice) && numericPrice >= 1000) {
    return numericPrice / 100;
  }

  return numericPrice;
};

const formatPrice = price => `R ${normalizePrice(price).toFixed(2)}`;

const getRestaurantId = restaurant => {
  return restaurant?.id || restaurant?._id || restaurant?.restaurantId || null;
};

const getRestaurantLatitude = restaurant => {
  return (
    restaurant?.latitude ||
    restaurant?.lat ||
    restaurant?.coordinates?.latitude ||
    restaurant?.location?.latitude ||
    restaurant?.location?.lat ||
    null
  );
};

const getRestaurantLongitude = restaurant => {
  return (
    restaurant?.longitude ||
    restaurant?.lng ||
    restaurant?.coordinates?.longitude ||
    restaurant?.location?.longitude ||
    restaurant?.location?.lng ||
    null
  );
};

const RestaurantDetails = () => {
  const {id} = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const {outlet, selectedTab} = location.state || {};

  const [restaurant, setRestaurant] = useState(outlet || null);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restaurantLoading, setRestaurantLoading] = useState(!outlet);
  const [cartBannerVisible, setCartBannerVisible] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const {addToCart, removeFromCart, cartItems} = useCart();

  useEffect(() => {
    if (!restaurant) {
      const fetchRestaurant = async () => {
        try {
          setRestaurantLoading(true);
          setErrorMessage('');

          const res = await api.get(`/restaurants/${id}`);
          setRestaurant(res?.data?.data || res?.data || null);
        } catch (err) {
          console.error('Failed to fetch restaurant:', err);
          setErrorMessage(
            err?.response?.data?.message ||
              err?.message ||
              'Failed to load restaurant.',
          );
        } finally {
          setRestaurantLoading(false);
        }
      };

      fetchRestaurant();
    }
  }, [id, restaurant]);

  useEffect(() => {
    if (restaurant) {
      const fetchMenu = async () => {
        try {
          setLoading(true);
          setErrorMessage('');

          const restaurantId = getRestaurantId(restaurant) || id;

          const response = await api.get(
            `/restaurants/${restaurantId}/menu-items`,
          );

          const items = response?.data?.data || response?.data || [];
          setMenuItems(Array.isArray(items) ? items : []);
        } catch (error) {
          console.error('Error fetching menu items:', error);
          setErrorMessage(
            error?.response?.data?.message ||
              error?.message ||
              'Failed to load menu items.',
          );
        } finally {
          setLoading(false);
        }
      };

      fetchMenu();
    }
  }, [restaurant, id]);

  useEffect(() => {
    const hasItems = cartItems.some(item => item.quantity > 0);

    if (hasItems) {
      setCartBannerVisible(true);
    }
  }, [cartItems]);

  const buildCartItem = item => {
    const restaurantId = getRestaurantId(restaurant) || id;
    const restaurantName = restaurant?.name || 'YaBatho Outlet';

    return {
      id: item?.id || item?._id,
      menuItemId: item?.id || item?._id,
      name: item?.name || 'Unnamed Item',
      description: item?.description || '',
      price: normalizePrice(item?.price),
      imageUrl: item?.imageUrl || item?.image || '',
      category: item?.category || '',
      restaurantId,
      restaurantName,
      restaurantData: restaurant,
      restaurantLatitude: getRestaurantLatitude(restaurant),
      restaurantLongitude: getRestaurantLongitude(restaurant),
      selectedMode: selectedTab || 'Delivery',
    };
  };

  const handleAddToCart = item => {
    const cartItem = buildCartItem(item);

    if (!cartItem.id || !cartItem.restaurantId) {
      setErrorMessage('This item cannot be added because restaurant details are missing.');
      return;
    }

    addToCart(cartItem);
    setCartBannerVisible(true);
  };

  const handleRemoveFromCart = itemId => {
    removeFromCart(itemId);
  };

  const hasCartItems = cartItems.some(item => item.quantity > 0);
  const lastCartItem = cartItems[cartItems.length - 1];

  const menuSections = useMemo(() => {
    const groups = menuItems
      .filter(item => (item?.isAvailable ?? item?.is_available) !== false)
      .reduce((result, item) => {
        const category = inferCategory(item);
        if (!result.has(category)) result.set(category, []);
        result.get(category).push(item);
        return result;
      }, new Map());

    return Array.from(groups, ([title, items]) => ({
      title,
      items: items.sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''))),
    })).sort((a, b) => {
      const aIndex = CATEGORY_ORDER.indexOf(a.title);
      const bIndex = CATEGORY_ORDER.indexOf(b.title);
      if (aIndex === -1 && bIndex === -1) return a.title.localeCompare(b.title);
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });
  }, [menuItems]);

  const visibleMenuSections = selectedCategory === 'all'
    ? menuSections
    : menuSections.filter(section => section.title === selectedCategory);

  if (restaurantLoading) {
    return (
      <div className={css(styles.page)}>
        <div className={css(styles.stateCard)}>
          <div className={css(styles.logoBadge)}>
            <img
              src={logo}
              alt="YaBatho Logo"
              className={css(styles.logoImage)}
            />
          </div>
          <h2 className={css(styles.stateTitle)}>Loading restaurant</h2>
          <p className={css(styles.stateText)}>
            Please wait while we prepare this outlet.
          </p>
        </div>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className={css(styles.page)}>
        <div className={css(styles.stateCard)}>
          <div className={css(styles.errorBadge)}>!</div>
          <h2 className={css(styles.stateTitle)}>Restaurant not found</h2>
          <p className={css(styles.stateText)}>
            {errorMessage || 'We could not find this restaurant.'}
          </p>
          <button
            type="button"
            className={css(styles.primaryButton)}
            onClick={() => navigate(-1)}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const restaurantName = restaurant?.name || 'YaBatho Outlet';
  const restaurantImage =
    restaurant?.imageUrl || restaurant?.uri || PLACEHOLDER_IMAGE;
  const restaurantAddress = restaurant?.address || 'Address not available';
  const restaurantDescription =
    restaurant?.description ||
    `Explore meals from ${restaurantName}, prepared for customers ordering through YaBatho.`;
  const rating = restaurant?.rating || '4.5';
  const deliveryTime = restaurant?.deliveryTime || '30–45 min';

  return (
    <div className={css(styles.page)}>
      <main className={css(styles.shell)}>
        <button
          type="button"
          className={css(styles.backButton)}
          onClick={() => navigate(-1)}>
          <FiArrowLeft />
          Back
        </button>

        <section className={css(styles.heroCard)}>
          <div className={css(styles.imageWrap)}>
            <img
              src={restaurantImage}
              alt={restaurantName}
              className={css(styles.heroImage)}
            />

            <div className={css(styles.imageOverlay)}>
              <span className={css(styles.modeBadge)}>
                {selectedTab || 'Delivery'}
              </span>
            </div>
          </div>

          <div className={css(styles.restaurantInfo)}>
            <p className={css(styles.kicker)}>YaBatho Outlet</p>

            <h1 className={css(styles.restaurantName)}>{restaurantName}</h1>

            <p className={css(styles.address)}>{restaurantAddress}</p>

            <div className={css(styles.metaRow)}>
              <div className={css(styles.metaItem)}>
                <span><FiStar /></span>
                <strong>{rating}</strong>
              </div>

              <div className={css(styles.metaDivider)} />

              <div className={css(styles.metaItem)}>
                <span><FiClock /></span>
                <strong>{deliveryTime}</strong>
              </div>
            </div>

            <div className={css(styles.aboutBox)}>
              <h3 className={css(styles.aboutTitle)}>About this outlet</h3>
              <p className={css(styles.description)}>
                {restaurantDescription}
              </p>
            </div>
          </div>
        </section>

        <section className={css(styles.menuSection)}>
          <div className={css(styles.menuHeader)}>
            <div>
              <p className={css(styles.sectionKicker)}>Menu</p>
              <h2 className={css(styles.sectionTitle)}>
                Available menu items
              </h2>
            </div>

            <span className={css(styles.countBadge)}>
              {menuItems.length} item{menuItems.length === 1 ? '' : 's'}
            </span>
          </div>

          {loading ? (
            <div className={css(styles.menuState)}>
              <div className={css(styles.logoBadge)}>
                <img
                  src={logo}
                  alt="YaBatho Logo"
                  className={css(styles.logoImage)}
                />
              </div>
              <p className={css(styles.stateTitle)}>Loading menu</p>
              <p className={css(styles.stateText)}>
                Please wait while we fetch the menu.
              </p>
            </div>
          ) : errorMessage ? (
            <div className={css(styles.menuState)}>
              <div className={css(styles.errorBadge)}>!</div>
              <p className={css(styles.stateTitle)}>Could not load menu</p>
              <p className={css(styles.stateText)}>{errorMessage}</p>
            </div>
          ) : menuSections.length > 0 ? (
            <>
              <label className={css(styles.categoryPickerLabel)} htmlFor="menu-category">
                Browse category
              </label>
              <div className={css(styles.categoryPickerWrap)}>
                <select
                  id="menu-category"
                  className={css(styles.categoryPicker)}
                  value={selectedCategory}
                  onChange={event => setSelectedCategory(event.target.value)}>
                  <option value="all">All Categories</option>
                  {menuSections.map(section => (
                    <option key={section.title} value={section.title}>
                      {section.title}
                    </option>
                  ))}
                </select>
              </div>

              {visibleMenuSections.map(section => (
                <div key={section.title} className={css(styles.categorySection)}>
                  <div className={css(styles.categoryHeader)}>
                    <h3 className={css(styles.categoryTitle)}>{section.title}</h3>
                    <span className={css(styles.categoryCount)}>
                      {section.items.length} {section.items.length === 1 ? 'item' : 'items'}
                    </span>
                  </div>

                  <div className={css(styles.menuGrid)}>
              {section.items.map(item => {
                const itemId = item.id || item._id;
                const cartItem = cartItems.find(i => i.id === itemId) || {
                  quantity: 0,
                };

                const itemPrice = normalizePrice(item.price);
                const totalPrice = itemPrice * cartItem.quantity;

                return (
                  <article key={itemId} className={css(styles.menuItem)}>
                    <div className={css(styles.menuItemMain)}>
                      <div className={css(styles.menuImageWrap)}>
                        <img
                          src={
                            item?.imageUrl ||
                            item?.image ||
                            'https://via.placeholder.com/160x160.png?text=Menu'
                          }
                          alt={item?.name || 'Menu item'}
                          className={css(styles.menuImage)}
                        />
                      </div>

                      <div className={css(styles.menuDetails)}>
                        <h3 className={css(styles.itemName)}>
                          {item?.name || 'Unnamed Item'}
                        </h3>

                        <p className={css(styles.itemDescription)}>
                          {item?.description || 'No description available.'}
                        </p>

                      </div>
                    </div>

                    <div className={css(styles.menuActions)}>
                      <div>
                        <p className={css(styles.price)}>
                          {formatPrice(itemPrice)}
                        </p>

                        {cartItem.quantity > 0 ? (
                          <p className={css(styles.itemTotal)}>
                            Total: {formatPrice(totalPrice)}
                          </p>
                        ) : null}
                      </div>

                      <div className={css(styles.controls)}>
                        <button
                          type="button"
                          className={css(styles.controlButton)}
                          onClick={() => handleRemoveFromCart(itemId)}>
                          <FiMinus />
                        </button>

                        <span className={css(styles.quantity)}>
                          {cartItem.quantity}
                        </span>

                        <button
                          type="button"
                          className={css(styles.controlButton)}
                          onClick={() => handleAddToCart(item)}>
                          +
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className={css(styles.menuState)}>
              <div className={css(styles.emptyBadge)}><FiCoffee /></div>
              <p className={css(styles.stateTitle)}>No menu items found</p>
              <p className={css(styles.stateText)}>
                This outlet has no menu items available right now.
              </p>
            </div>
          )}
        </section>
      </main>

      {hasCartItems && cartBannerVisible && (
        <CartBannerWeb
          item={lastCartItem}
          navigate={navigate}
          onClose={() => setCartBannerVisible(false)}
        />
      )}
    </div>
  );
};

const styles = StyleSheet.create({
  page: {
    minHeight: '100vh',
    width: '100%',
    backgroundColor: '#FFFDF7',
    color: '#0F3D2E',
    fontFamily: 'Inter, Segoe UI, Arial, sans-serif',
    boxSizing: 'border-box',
    padding: '24px 24px 120px',

    '@media (max-width: 720px)': {
      padding: '16px 14px 116px',
    },
  },

  shell: {
    width: '100%',
    maxWidth: '1180px',
    margin: '0 auto',
  },

  backButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '11px 16px',
    borderRadius: '999px',
    border: '1px solid #E6D8BE',
    backgroundColor: '#ffffff',
    color: '#0F3D2E',
    fontSize: '14px',
    fontWeight: '900',
    cursor: 'pointer',
    marginBottom: '18px',

    ':hover': {
      borderColor: '#C9A14A',
      transform: 'translateY(-1px)',
    },
  },

  heroCard: {
    backgroundColor: '#ffffff',
    border: '1px solid #E6D8BE',
    borderRadius: '34px',
    overflow: 'hidden',
    boxShadow: '0 16px 42px rgba(30, 30, 30, 0.07)',
    display: 'grid',
    gridTemplateColumns: '1fr 0.85fr',
    marginBottom: '24px',

    '@media (max-width: 860px)': {
      gridTemplateColumns: '1fr',
    },
  },

  imageWrap: {
    position: 'relative',
    minHeight: '380px',
    backgroundColor: '#F4EBD8',

    '@media (max-width: 720px)': {
      minHeight: '260px',
    },
  },

  heroImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },

  imageOverlay: {
    position: 'absolute',
    top: '18px',
    left: '18px',
    right: '18px',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },

  modeBadge: {
    backgroundColor: 'rgba(15, 61, 46, 0.94)',
    color: '#C9A14A',
    border: '1px solid rgba(201, 161, 74, 0.45)',
    borderRadius: '999px',
    padding: '9px 13px',
    fontSize: '11px',
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: '0.6px',
  },

  restaurantInfo: {
    padding: '30px',
    boxSizing: 'border-box',

    '@media (max-width: 720px)': {
      padding: '22px',
    },
  },

  kicker: {
    margin: '0 0 8px',
    color: '#C9A14A',
    fontSize: '12px',
    fontWeight: '900',
    letterSpacing: '1px',
    textTransform: 'uppercase',
  },

  restaurantName: {
    margin: 0,
    color: '#0F3D2E',
    fontSize: '42px',
    lineHeight: 1.05,
    letterSpacing: '-1.8px',
    fontWeight: '900',

    '@media (max-width: 720px)': {
      fontSize: '32px',
    },
  },

  address: {
    margin: '12px 0 0',
    color: '#6E6254',
    fontSize: '15px',
    lineHeight: 1.65,
  },

  metaRow: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '12px',
    marginTop: '18px',
    padding: '12px 14px',
    borderRadius: '20px',
    backgroundColor: '#FFFDF7',
    border: '1px solid #E6D8BE',
  },

  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: '#0F3D2E',
    fontSize: '13px',
  },

  metaDivider: {
    width: '1px',
    height: '20px',
    backgroundColor: '#E6D8BE',
  },

  aboutBox: {
    marginTop: '22px',
    backgroundColor: '#FFFDF7',
    border: '1px solid #E6D8BE',
    borderRadius: '24px',
    padding: '18px',
  },

  aboutTitle: {
    margin: '0 0 8px',
    color: '#0F3D2E',
    fontSize: '17px',
    fontWeight: '900',
  },

  description: {
    margin: 0,
    color: '#6E6254',
    fontSize: '14px',
    lineHeight: 1.7,
  },

  menuSection: {
    backgroundColor: '#ffffff',
    border: '1px solid #E6D8BE',
    borderRadius: '32px',
    padding: '22px',
    boxShadow: '0 12px 34px rgba(30, 30, 30, 0.06)',

    '@media (max-width: 720px)': {
      padding: '16px',
      borderRadius: '26px',
    },
  },

  menuHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
    marginBottom: '18px',

    '@media (max-width: 640px)': {
      flexDirection: 'column',
    },
  },

  sectionKicker: {
    margin: '0 0 6px',
    color: '#C9A14A',
    fontSize: '11px',
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: '0.9px',
  },

  sectionTitle: {
    margin: 0,
    color: '#0F3D2E',
    fontSize: '26px',
    fontWeight: '900',
    letterSpacing: '-0.8px',

    '@media (max-width: 520px)': {
      fontSize: '22px',
    },
  },

  countBadge: {
    backgroundColor: '#F4EBD8',
    border: '1px solid #E6D8BE',
    color: '#0F3D2E',
    borderRadius: '999px',
    padding: '9px 14px',
    fontSize: '12px',
    fontWeight: '900',
    whiteSpace: 'nowrap',
  },

  categoryPickerLabel: {
    display: 'block',
    margin: '0 0 7px',
    color: '#8C8170',
    fontSize: '11px',
    fontWeight: '900',
    letterSpacing: '0.7px',
    textTransform: 'uppercase',
  },

  categoryPickerWrap: {
    position: 'relative',
    marginBottom: '24px',
  },

  categoryPicker: {
    width: '100%',
    minHeight: '52px',
    boxSizing: 'border-box',
    padding: '0 16px',
    color: '#0F3D2E',
    backgroundColor: '#FFFDF7',
    border: '1px solid #E6D8BE',
    borderRadius: '18px',
    outline: 'none',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: '900',

    ':focus': {
      borderColor: '#C9A14A',
      boxShadow: '0 0 0 3px rgba(201, 161, 74, 0.15)',
    },
  },

  categorySection: {
    marginTop: '24px',
  },

  categoryHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    marginBottom: '12px',
    paddingBottom: '10px',
    borderBottom: '1px solid #E6D8BE',
  },

  categoryTitle: {
    margin: 0,
    color: '#0F3D2E',
    fontSize: '20px',
    fontWeight: '900',
    letterSpacing: '-0.4px',
  },

  categoryCount: {
    color: '#8C8170',
    fontSize: '12px',
    fontWeight: '800',
  },

  menuGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '14px',
  },

  menuItem: {
    backgroundColor: '#FFFDF7',
    border: '1px solid #E6D8BE',
    borderRadius: '24px',
    padding: '14px',
    display: 'grid',
    gridTemplateColumns: '1fr auto',
    gap: '16px',
    alignItems: 'center',

    '@media (max-width: 720px)': {
      gridTemplateColumns: '1fr',
    },
  },

  menuItemMain: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    minWidth: 0,
  },

  menuImageWrap: {
    width: '86px',
    height: '86px',
    minWidth: '86px',
    borderRadius: '20px',
    overflow: 'hidden',
    backgroundColor: '#F4EBD8',
    border: '1px solid #E6D8BE',
  },

  menuImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },

  menuDetails: {
    minWidth: 0,
  },

  itemName: {
    margin: '0 0 6px',
    color: '#0F3D2E',
    fontSize: '17px',
    lineHeight: 1.25,
    fontWeight: '900',
  },

  itemDescription: {
    margin: '0 0 8px',
    color: '#6E6254',
    fontSize: '13px',
    lineHeight: 1.55,
  },

  categoryBadge: {
    display: 'inline-flex',
    backgroundColor: '#F4EBD8',
    border: '1px solid #E6D8BE',
    color: '#0F3D2E',
    borderRadius: '999px',
    padding: '6px 10px',
    fontSize: '11px',
    fontWeight: '900',
  },

  menuActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    justifyContent: 'flex-end',
    minWidth: 0,

    '@media (max-width: 720px)': {
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: '12px',
    },
  },

  price: {
    margin: 0,
    color: '#0F3D2E',
    fontSize: '17px',
    fontWeight: '900',
    textAlign: 'right',
    whiteSpace: 'nowrap',

    '@media (max-width: 720px)': {
      textAlign: 'left',
    },
  },

  itemTotal: {
    margin: '5px 0 0',
    color: '#C9A14A',
    fontSize: '12px',
    fontWeight: '900',
    textAlign: 'right',
    lineHeight: 1.35,

    '@media (max-width: 720px)': {
      textAlign: 'left',
    },
  },

  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: '#ffffff',
    border: '1px solid #E6D8BE',
    borderRadius: '999px',
    padding: '6px',
    flexShrink: 0,
  },

  controlButton: {
    width: '34px',
    height: '34px',
    borderRadius: '50%',
    border: 'none',
    backgroundColor: '#C9A14A',
    color: '#0F3D2E',
    fontSize: '18px',
    fontWeight: '900',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    ':hover': {
      transform: 'translateY(-1px)',
    },
  },

  quantity: {
    minWidth: '24px',
    textAlign: 'center',
    color: '#0F3D2E',
    fontSize: '14px',
    fontWeight: '900',
  },

  menuState: {
    width: '100%',
    minHeight: '230px',
    backgroundColor: '#FFFDF7',
    border: '1px solid #E6D8BE',
    borderRadius: '26px',
    padding: '32px 24px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
  },

  stateCard: {
    width: '100%',
    maxWidth: '560px',
    margin: '80px auto 0',
    backgroundColor: '#ffffff',
    border: '1px solid #E6D8BE',
    borderRadius: '30px',
    padding: '34px 24px',
    boxSizing: 'border-box',
    textAlign: 'center',
    boxShadow: '0 12px 34px rgba(30, 30, 30, 0.06)',
  },

  logoBadge: {
    width: '78px',
    height: '78px',
    borderRadius: '50%',
    border: '2px solid #C9A14A',
    margin: '0 auto 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    boxShadow: '0 14px 30px rgba(0, 0, 0, 0.08)',

    '@media (max-width: 520px)': {
      width: '68px',
      height: '68px',
    },
  },

  logoImage: {
    width: '62px',
    height: '62px',
    objectFit: 'contain',
    display: 'block',

    '@media (max-width: 520px)': {
      width: '54px',
      height: '54px',
    },
  },

  emptyBadge: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    backgroundColor: '#0F3D2E',
    border: '2px solid #C9A14A',
    color: '#C9A14A',
    margin: '0 auto 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
  },

  errorBadge: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    backgroundColor: '#FFE9E9',
    border: '2px solid #F5B8B8',
    color: '#D9534F',
    margin: '0 auto 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    fontWeight: '900',
  },

  stateTitle: {
    margin: '0 0 8px',
    color: '#0F3D2E',
    fontSize: '20px',
    fontWeight: '900',
  },

  stateText: {
    margin: 0,
    color: '#6E6254',
    fontSize: '14px',
    lineHeight: 1.7,
  },

  primaryButton: {
    marginTop: '18px',
    padding: '13px 20px',
    borderRadius: '18px',
    border: 'none',
    backgroundColor: '#C9A14A',
    color: '#0F3D2E',
    fontSize: '14px',
    fontWeight: '900',
    cursor: 'pointer',
  },
});

export default RestaurantDetails;
